import { useMediaQuery } from '@mui/material';
import jwt_decode from 'jwt-decode';
import { observer } from 'mobx-react-lite';
import { FC, PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AnimationWrapper, AppContainer, SpeedDialIconsWrapper } from './App.styles';
import { getCanAccessPath } from './App.util';
import { RoutePaths, SIDE_MENU_EVENT, SPEED_DIAL_EVENT } from './constants';
import { LocalStorageKey } from './constants/localStorageKey';
import { useEventContext } from './global-state/eventContext';
import { useThemeContext } from './global-state/themeContext';
import { Client, isValidProfile, RoleMatrix } from './global-state/types';
import {
  useLocalStorage,
  useProfile,
  useRoleMatrix,
  useStoreInformation,
  useStores,
  useTokenInfo,
  useUser,
} from './hooks';
import useEventTracker from './hooks/useEventTracker';
import { useOfflineBanner } from './hooks/useOfflineBanner';
import { AccessRequestDetails, useUserPermissions } from './hooks/useUserPermissions';
import { Development, Employees, Home, NotFound, ProductCategoryPage, TransactionSearch } from './pages';
import { AccessApprovalPage } from './pages/accessApproval/AccessApprovalPage';
import { AreaOverview } from './pages/areaOverview/AreaOverview';
import { AreaResultPage } from './pages/areaResult/AreaResultPage';
import { AreaStoresListPage } from './pages/areaStoresList/AreaStoresListPage';
import { KpiTarget } from './pages/kpiTarget/KpiTarget';
import { Profile } from './pages/profile/Profile';
import { ProfitAndLoss } from './pages/profitAndLoss/ProfitAndLoss';
import { RequestNewRole } from './pages/requestNewRole/RequestNewRole';
import { RequestsStatusView } from './pages/requestNewRole/requestsStatusView/RequestsStatusView';
import { SalesAnalysisPage } from './pages/salesAnalysis/SalesAnalysisPage';
import { SelectStore } from './pages/select-store/SelectStore';
import { SoldProductsPage } from './pages/soldProducts/SoldProductsPage';
import { StoreOverviewPage } from './pages/storeOverviewPage/StoreOverviewPage';
import { WidgetAccessControlPage } from './pages/widgetAccessControl/WidgetAccessControlPage';
import { YearlyStockTakePage } from './pages/yearlyStockTake/YearlyStockTakePage';
import { Chatbot } from './speedDial/chatbot/Chatbot';
import { FeedbackForm } from './speedDial/feedbackForm/FeedbackForm';
import { HomePageSlider } from './speedDial/homePageSlider/HomePageSlider';
import { Container, Icon, Icons, Typography } from './stories/atoms';
import { ColumnFlexContainer } from './stories/atoms/containerV2/ContainerV2';
import { Animation, Animations, Banner, SpeedDialActionDetails, SpeedDialButton } from './stories/molecules';
import { ActionButton, BannerItemsType } from './stories/molecules/banner';
import { ErrorFallback, Header, SideMenu } from './stories/organisms';
import { muiTheme } from './theme';
import { commonTranslations, errors, homeTranslations, selectStoreTranslation } from './translations';
import { sideMenuTranslations } from './translations/navigation';
import {
  getUniqueId,
  getUserPermissions,
  isFailureResponse,
  parseExtensionAttributeAssignements,
  parseJWTToken,
  Translation,
} from './utils';
import { ErrorType, logError } from './utils/splunkLogger';

type SpeedDialActionType = 'STORE_DETAILS' | 'CHAT_BOT' | 'FEEDBACK';

type Link = {
  path: RoutePaths;
  element: ReactNode;
  translation: Translation;
  icon: Icons;
  badgeIcon?: JSX.Element;
};

const speedDialActions: SpeedDialActionDetails<SpeedDialActionType>[] = [
  {
    text: 'STORE_DETAILS',
    icon: <Icon icon={Icons.INFO} />,
    tooltipText: <Typography color="white">General Information</Typography>,
  },
  {
    text: 'CHAT_BOT',
    icon: <Icon icon={Icons.CHATBOT} />,
    tooltipText: <Typography color="white">Ask a question</Typography>,
  },
  {
    text: 'FEEDBACK',
    icon: <Icon icon={Icons.FEEDBACK} />,
    tooltipText: <Typography color="white">Provide Feedback </Typography>,
  },
];

export type RouteList = {
  section: Translation;
  links: Link[];
};

const {
  test,
  transaction,
  employees,
  home,
  areaOverview,
  widgets,
  areaResult,
  storesList,
  general,
  dashboards,
  kpiTarget,
  soldProducts,
  storePerformanceInsights,
  productCategory,
  profitAndLoss,
  widgetAccessControl,
  salesAnalysis,
  yearlyStockTake,
  profile: profileTranslation,
  accessApproval,
  storeOverviewPage,
} = sideMenuTranslations;

export const App: FC<PropsWithChildren<unknown>> = observer(() => {
  const {
    set,
    get: { currentStoreId, storesDetails, profile, userPermissions },
  } = useUser();
  const { mode } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchProfile } = useProfile();
  const { fetchStoresDetails, fetchStoresData, fetchHighlightedKpi } = useStores();
  const { fetchRoleMatrix } = useRoleMatrix();
  const { fetchUserPermissions, fetchRequestedAssignments } = useUserPermissions();
  const { getItem, setItem, removeItem } = useLocalStorage();
  const { t, i18n } = useTranslation();
  const { registerEvent } = useEventContext();
  const { exclusionListCount } = useStoreInformation();
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('desktopLarge'));
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));
  const trackEvent = useEventTracker();
  const { accessToken: token, idToken: dataToken, fetchTokenInfo } = useTokenInfo();
  const [currentProfileUpn, setCurrentProfileUpn] = useState<string | null>(null);

  const [isError, setIsError] = useState(false);
  const [isRequestedAssignmentsError, setIsRequestedAssignmentsError] = useState(false);
  const [isAssignmentError, setIsAssignmentError] = useState(false);
  const [isStorePermissionError, setIsStorePermissionError] = useState(false);
  const [isStoresEmpty, setIsStoresEmpty] = useState(false);
  const [requestedAssignments, setRequestedAssignments] = useState<AccessRequestDetails>();

  const [isFetchingProfile, setIsFetchingProfile] = useState<boolean>(true);
  const [isFetchingStoresDetails, setIsFetchingStoresDetails] = useState(false);
  const [isFetchingStoresData, setIsFetchingStoresData] = useState(false);
  const [isFetchingRoleMatrix, setIsFetchingRoleMatrix] = useState<boolean>(false);
  const [isFetchingPermissions, setIsFetchingPermissions] = useState<boolean>(false);
  const [isFetchingHighlightedWidgets, setIsFetchingHighlightedWidgets] = useState<boolean>(false);
  const [isFetchingRequestedAssignments, setIsFetchingRequestedAssignments] = useState<boolean>(false);

  const [bannerItems, setBannerItems] = useState<BannerItemsType[]>([]);
  const [systemBanners, setSystemBanners] = useState<BannerItemsType[]>([]);
  const [isSliderOpen, setIsSliderOpen] = useState(false);

  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const openChatbot = () => setIsChatbotOpen(true);
  const closeChatbot = () => setIsChatbotOpen(false);

  const [isHomePageSliderOpen, setIsHomePageSliderOpen] = useState(false);
  const openHomePageSlider = () => setIsHomePageSliderOpen(true);
  const closeHomePageSlider = () => setIsHomePageSliderOpen(false);

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const openFeedback = () => setIsFeedbackOpen(true);
  const closeFeedback = () => setIsFeedbackOpen(false);

  const [showRequestAccess, setShowRequestAccess] = useState(false);

  const handleOnAction = (actionType: SpeedDialActionType) => {
    switch (actionType) {
      case 'CHAT_BOT':
        openChatbot();
        trackEvent(SPEED_DIAL_EVENT, 'click', [{ name: 'source', stringValue: 'Chat_Bot' }]);
        break;
      case 'STORE_DETAILS':
        openHomePageSlider();
        trackEvent(SPEED_DIAL_EVENT, 'click', [{ name: 'source', stringValue: 'General_Information' }]);
        break;
      case 'FEEDBACK':
        openFeedback();
        trackEvent(SPEED_DIAL_EVENT, 'click', [{ name: 'source', stringValue: 'Feedback' }]);
        break;
    }
  };

  const selectedView = getItem(LocalStorageKey.SelectedView);
  const isArea = selectedView === 'area';

  const canViewSalesPage = userPermissions?.salesOverviewPage?.canRead;

  const RouteConfigMap = {
    [RoutePaths.HOME]: {
      path: RoutePaths.HOME,
      translation: selectedView === 'area' ? areaOverview : canViewSalesPage ? storeOverviewPage : home,
      element: selectedView === 'area' ? <AreaOverview /> : canViewSalesPage ? <StoreOverviewPage /> : <Home />,
      icon: selectedView === 'area' ? Icons.HOME : canViewSalesPage ? Icons.MONEYBAG : Icons.HOME,
    },
    [RoutePaths.WIDGETS]: {
      path: RoutePaths.WIDGETS,
      translation: widgets,
      element: <Home />,
      icon: Icons.GRID,
    },
    [RoutePaths.WIDGETACCESSCONTROL]: {
      path: RoutePaths.WIDGETACCESSCONTROL,
      translation: widgetAccessControl,
      element: <WidgetAccessControlPage />,
      icon: Icons.SETTINGS,
    },
    [RoutePaths.STORESLIST]: {
      path: RoutePaths.STORESLIST,
      translation: storesList,
      element: <AreaStoresListPage />,
      icon: Icons.STORE,
    },
    [RoutePaths.AREARESULT]: {
      path: RoutePaths.AREARESULT,
      translation: areaResult,
      element: <AreaResultPage />,
      icon: Icons.MAP,
    },
    [RoutePaths.EMPLOYEES]: {
      path: RoutePaths.EMPLOYEES,
      translation: employees,
      element: <Employees />,
      icon: Icons.HEART,
    },
    [RoutePaths.TRANSACTION]: {
      path: RoutePaths.TRANSACTION,
      translation: transaction,
      element: <TransactionSearch />,
      icon: Icons.TRANSACTION_SEARCH,
    },
    [RoutePaths.YEARLYSTOCKTAKE]: {
      path: RoutePaths.YEARLYSTOCKTAKE,
      translation: yearlyStockTake,
      element: <YearlyStockTakePage />,
      icon: Icons.DROPBOX,
    },
    [RoutePaths.SALESANALYSIS]: {
      path: RoutePaths.SALESANALYSIS,
      translation: salesAnalysis,
      element: <SalesAnalysisPage />,
      icon: Icons.SALESEFFICIENCY,
    },
    [RoutePaths.KPITARGET]: {
      path: RoutePaths.KPITARGET,
      translation: kpiTarget,
      element: <KpiTarget />,
      icon: Icons.TARGET,
    },
    [RoutePaths.SOLDPRODUCTS]: {
      path: RoutePaths.SOLDPRODUCTS,
      translation: soldProducts,
      element: <SoldProductsPage />,
      icon: Icons.BAG,
    },
    [RoutePaths.PRODUCTCATEGORY]: {
      path: RoutePaths.PRODUCTCATEGORY,
      translation: productCategory,
      element: <ProductCategoryPage />,
      icon: Icons.PRODUCTCATEGORY,
    },
    [RoutePaths.PROFITANDLOSS]: {
      path: RoutePaths.PROFITANDLOSS,
      translation: profitAndLoss,
      element: <ProfitAndLoss />,
      icon: Icons.PROFITANDLOSS,
    },
    [RoutePaths.PROFILE]: {
      path: RoutePaths.PROFILE,
      translation: profileTranslation,
      element: <Profile />,
      icon: Icons.PROFILE,
    },
    [RoutePaths.ACCESSREQUESTS]: {
      path: RoutePaths.ACCESSREQUESTS,
      translation: accessApproval,
      element: <AccessApprovalPage />,
      icon: Icons.HOURLASS,
    },
    [RoutePaths.DEVELOPMENT]: {
      path: RoutePaths.DEVELOPMENT,
      translation: { key: 'development' },
      element: <Development />,
      icon: Icons.WARNING,
    },
  };

  const path = location.pathname.split('/')[1] as RoutePaths;
  const currentRouteConfig = Object.values(RouteConfigMap).find((route) => route.path.includes(path));

  const isUserPermissionsLoaded = userPermissions && Object.keys(userPermissions).length > 0;

  const getSpeeddialPermission = () => {
    if (isUserPermissionsLoaded && userPermissions?.chatBoxPage.canRead) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!i18n.resolvedLanguage) {
      setSystemBanners(() => [
        {
          id: getUniqueId(),
          label: t(
            homeTranslations.banners.translationError.key,
            homeTranslations.banners.translationError.defaultValue,
          ),
          type: 'warning',
          actionButton: ActionButton.Close,
          hasNoMargin: true,
        },
      ]);
    }
  }, [i18n.resolvedLanguage]);

  useOfflineBanner(setSystemBanners);

  const triggerFetchProfile = async () => {
    if (!token) return;

    const { upn } = parseJWTToken<{ upn: string }>(token);
    const response = await fetchProfile(upn);

    if (isFailureResponse(response)) {
      setIsError(true);
      return null;
    }

    set({ profile: response.data });
    setIsFetchingProfile(false);
  };

  useEffect(() => {
    triggerFetchProfile();
  }, [token]);

  useEffect(() => {
    if (!dataToken) return;

    const {
      given_name: firstName,
      family_name: lastName,
      upn,
      ExtensionAttribute7,
      roles,
    } = jwt_decode(dataToken) as {
      given_name: string;
      family_name: string;
      upn: string;
      ExtensionAttribute7: string;
      roles?: string[];
    };

    const assignments = parseExtensionAttributeAssignements(ExtensionAttribute7);

    const isAppRole = roles?.includes('Developer'); // For now checking developer role as they dont have extension attribute
    if (roles) {
      const defaultIndex = roles.indexOf('Default');
      if (defaultIndex > -1) {
        roles?.splice(defaultIndex, 1);
      }
    }

    set({
      profile: {
        ...profile,
        firstName,
        lastName,
        upn,
        assignments: isAppRole ? profile?.assignments : assignments,
        appRoles: roles,
      },
    });
  }, [dataToken]);

  useEffect(() => {
    if (!isValidProfile(profile)) return;

    setCurrentProfileUpn(profile.upn);
  }, [profile]);

  useEffect(() => {
    if (!currentProfileUpn) return;

    triggerFetchStoresDetails();
  }, [currentProfileUpn]);

  // Trigger register event when storeId or upn changes
  useEffect(() => {
    if (!profile?.upn || !currentStoreId) return;

    registerEvent(profile?.upn, currentStoreId);
  }, [profile?.upn, currentStoreId]);

  useEffect(() => {
    if (!token || !currentStoreId) return;

    const getRoleMatrix = async () => {
      const clients: Client[] = ['box'];
      setIsFetchingRoleMatrix(true);
      const roleMatrixes = await Promise.all(
        clients.map(async (client) => {
          const marketCode = currentStoreId.slice(0, 2);
          const response = await fetchRoleMatrix(client, marketCode);

          if (isFailureResponse(response)) {
            setIsError(true);
            return null;
          }

          const { data } = response;
          const key = data.modelKey?.split('-')[0];

          return { [key]: data };
        }),
      );
      setIsFetchingRoleMatrix(false);
      if (roleMatrixes.some((matrix) => !matrix)) return;

      const updatedRoleMatrix = roleMatrixes.reduce((previousMatrix, currentMatrix) => ({
        ...previousMatrix,
        ...currentMatrix,
      })) as RoleMatrix;

      set({ roleMatrix: updatedRoleMatrix });
    };

    getRoleMatrix();

    const triggerFetchUserPermissions = async () => {
      setIsFetchingPermissions(true);
      const upn = profile.upn;
      const response = await fetchUserPermissions(upn, currentStoreId);

      setIsFetchingPermissions(false);
      if (isFailureResponse(response)) {
        if (response.status === 404) {
          setIsStorePermissionError(true);
          return;
        }
        setIsError(true);
        return null;
      }

      const apiUserPermissions = getUserPermissions(response.data);

      set({ userPermissions: apiUserPermissions });
    };

    triggerFetchUserPermissions();
  }, [token, currentStoreId, profile?.assignments, profile?.appRoles]);

  useEffect(() => {
    fetchTokenInfo();
  }, []);

  useEffect(() => {
    if (!currentStoreId) return;

    triggerFetchStoresData();
  }, [currentStoreId]);

  useEffect(() => {
    if (!currentStoreId) return;

    triggerFetchHighlightedKpi();
  }, [currentStoreId]);

  useEffect(() => {
    if (isStoresEmpty || isAssignmentError) {
      triggerFetchRequestedAssignments();
    }
  }, [isStoresEmpty, isAssignmentError]);

  // handle start page navigation
  useEffect(() => {
    if (!profile || !isUserPermissionsLoaded || isFetchingPermissions || selectedView === 'area') return;

    let startPage: string | undefined = profile.settings?.startPage;

    if (!startPage) {
      if (isMobile && canViewSalesPage) {
        startPage = RoutePaths.WIDGETS;
      } else {
        startPage = RoutePaths.HOME;
      }
    }

    const currentStoreDetail = storesDetails.find((store) => store.storeId === currentStoreId);

    if (
      !getCanAccessPath(
        startPage as RoutePaths,
        userPermissions,
        false,
        exclusionListCount,
        currentStoreDetail?.corporateBrandId ?? 0,
      )
    ) {
      startPage = RoutePaths.HOME;
    }

    if (startPage === RoutePaths.WIDGETS && !canViewSalesPage) {
      startPage = RoutePaths.HOME;
    }

    if (startPage === RoutePaths.EMPLOYEES) {
      startPage = `${RoutePaths.EMPLOYEES.replace(':storeId', currentStoreId)}`;
    }

    const currentPath = location.pathname;
    const canAccessCurrentPath = getCanAccessPath(
      currentPath as RoutePaths,
      userPermissions,
      false,
      exclusionListCount,
      currentStoreDetail?.corporateBrandId ?? 0,
    );

    if (currentPath === '/') {
      navigate(startPage);
      return;
    }

    if (!canAccessCurrentPath) {
      navigate(startPage);
      return;
    }
  }, [profile, isFetchingPermissions, userPermissions, selectedView]);

  const triggerFetchStoresDetails = async () => {
    if (!profile.assignments?.length && !isFetchingProfile) {
      if (!profile?.appRoles) {
        setIsAssignmentError(true);
        return;
      } else return;
    }

    if (storesDetails && storesDetails.length > 0) return;

    setIsFetchingStoresDetails(true);

    const response = await fetchStoresDetails();

    setIsFetchingStoresDetails(false);

    if (isFailureResponse(response)) {
      setIsError(true);
      return null;
    }

    const { data: newStoresDetails } = response;

    if (newStoresDetails.length === 0) {
      setIsStoresEmpty(true);
      return;
    }

    const storeIdParam = location.pathname.replace('/home/', '');

    const hasAccess =
      storeIdParam && profile.assignments?.some((assignment) => assignment.locationCode === storeIdParam);

    if (hasAccess) {
      setItem(LocalStorageKey.SelectedStore, storeIdParam);
    }

    let storeId = newStoresDetails.find(
      ({ storeId: id }) => id === getItem(LocalStorageKey.SelectedStore),
    )?.storeId;

    if (!storeId) {
      storeId = newStoresDetails.length > 1 ? '' : (newStoresDetails[0]?.storeId as string);
    }

    set({
      storesDetails: newStoresDetails,
      currentStoreId: storeId,
    });
  };

  const triggerFetchStoresData = async () => {
    setIsFetchingStoresData(true);
    const response = await fetchStoresData(currentStoreId);
    setIsFetchingStoresData(false);

    if (isFailureResponse(response)) {
      setIsError(true);
      return null;
    }

    set({
      stores: [response.data],
    });
  };

  const triggerFetchHighlightedKpi = async () => {
    setIsFetchingHighlightedWidgets(true);
    const response = await fetchHighlightedKpi();
    setIsFetchingHighlightedWidgets(false);

    if (isFailureResponse(response)) {
      return null;
    }

    set({
      highlightedKpiTargets: response.data,
    });
  };

  const triggerFetchRequestedAssignments = async () => {
    setIsFetchingRequestedAssignments(true);
    const response = await fetchRequestedAssignments();
    setIsFetchingRequestedAssignments(false);

    if (isFailureResponse(response)) {
      setIsRequestedAssignmentsError(true);
      return null;
    }
    setRequestedAssignments({
      ...response.data,
      requests: response.data.requests ?? [],
    });
  };

  const filteredMenuList = () => {
    const routes: RouteList[] = [
      {
        section: dashboards,
        links: [
          RouteConfigMap[RoutePaths.HOME],
          ...(canViewSalesPage ? [RouteConfigMap[RoutePaths.WIDGETS]] : []),
          RouteConfigMap[RoutePaths.WIDGETACCESSCONTROL],
          RouteConfigMap[RoutePaths.STORESLIST],
          ...(isArea ? [RouteConfigMap[RoutePaths.AREARESULT]] : []),
        ],
      },
      {
        section: general,
        links: [
          RouteConfigMap[RoutePaths.EMPLOYEES],
          RouteConfigMap[RoutePaths.TRANSACTION],
          RouteConfigMap[RoutePaths.SALESANALYSIS],
          RouteConfigMap[RoutePaths.YEARLYSTOCKTAKE],
        ],
      },
      {
        section: storePerformanceInsights,
        links: [
          ...(!isArea ? [RouteConfigMap[RoutePaths.AREARESULT]] : []),
          RouteConfigMap[RoutePaths.KPITARGET],
          RouteConfigMap[RoutePaths.SOLDPRODUCTS],
          RouteConfigMap[RoutePaths.PRODUCTCATEGORY],
          RouteConfigMap[RoutePaths.PROFITANDLOSS],
        ],
      },
      {
        section: profileTranslation,
        links: [RouteConfigMap[RoutePaths.PROFILE], RouteConfigMap[RoutePaths.ACCESSREQUESTS]],
      },
      {
        section: test,
        links: [...(process.env.NODE_ENV === 'development' ? [RouteConfigMap[RoutePaths.DEVELOPMENT]] : [])],
      },
    ];

    const filteredList = routes.map((section) => {
      const currentStoreDetail = storesDetails.find((store) => store.storeId === currentStoreId);

      const formatLinks = section.links.filter((link) =>
        getCanAccessPath(
          link.path,
          userPermissions,
          false,
          exclusionListCount,
          currentStoreDetail?.corporateBrandId ?? 0,
        ),
      );

      return {
        ...section,
        links: formatLinks,
      };
    });

    return filteredList;
  };

  const isAccessRequestPath = location.pathname === RoutePaths.ACCESSREQUESTS;

  const selectedStore = getItem(LocalStorageKey.SelectedStore);

  if (
    isAccessRequestPath &&
    (isAssignmentError || isStoresEmpty || isRequestedAssignmentsError || isError || !selectedStore)
  ) {
    return (
      <ColumnFlexContainer padding={[4]} backgroundColor={mode === 'dark' ? 'backgroundDark' : 'baseGray'}>
        <Typography type="h4" padding={[4, 4, 0]}>
          {t(accessApproval.key, accessApproval.defaultValue)}
        </Typography>
        <AccessApprovalPage />
      </ColumnFlexContainer>
    );
  }

  if (!currentStoreId && storesDetails.length > 1 && !isAssignmentError) {
    return <SelectStore />;
  }

  const isFetching =
    isFetchingProfile ||
    isFetchingStoresDetails ||
    isFetchingStoresData ||
    isFetchingRoleMatrix ||
    isFetchingPermissions ||
    isFetchingHighlightedWidgets ||
    isFetchingRequestedAssignments;

  return (
    <>
      {isError && (
        <AnimationWrapper>
          <ErrorFallback />
        </AnimationWrapper>
      )}
      {isRequestedAssignmentsError && (
        <AnimationWrapper data-testid="no-requested-assignment-error">
          <Animation
            title={t(commonTranslations.errorText1.key, commonTranslations.errorText1.defaultValue)}
            subtitle={t(errors.noRequestedAssignments.key, errors.noRequestedAssignments.defaultValue)}
            animation={Animations.SAD}
          />
        </AnimationWrapper>
      )}
      {isStorePermissionError && (
        <AnimationWrapper data-testid="no-store-permission-error">
          <Animation
            title={t(commonTranslations.errorText1.key, commonTranslations.errorText1.defaultValue)}
            subtitle={t(
              selectStoreTranslation.permissionNetworkError.key,
              selectStoreTranslation.permissionNetworkError.defaultValue,
            )}
            animation={Animations.SAD}
            button={{
              label: t(
                selectStoreTranslation.viewStoresButton.key,
                selectStoreTranslation.viewStoresButton.defaultValue,
              ),
              onClick: () => {
                removeItem(LocalStorageKey.SelectedStore);
                navigate('/');
                window.location.reload();
              },
              buttonContentProps: {
                iconOptions: {
                  icon: Icons.STORE,
                  color: 'white',
                },
              },
            }}
          />
        </AnimationWrapper>
      )}
      {isAssignmentError && (
        <AnimationWrapper data-testid="no-assignment-error">
          <Animation
            title={t(errors.noAssignments.title.key, errors.noAssignments.title.defaultValue)}
            subtitle={t(errors.noAssignments.subtitle.key, errors.noAssignments.subtitle.defaultValue)}
            animation={Animations.SAD}
          />
        </AnimationWrapper>
      )}
      {isStoresEmpty && !isAssignmentError && (
        <AnimationWrapper data-testid="no-stores-error">
          {showRequestAccess ? (
            // Show RequestNewRoles when user clicked the button
            <Container
              backgroundColor={mode === 'dark' ? 'backgroundDark' : 'baseGray'}
              height="100%"
              position="center"
            >
              <RequestNewRole
                onRequestSuccess={async () => {
                  await triggerFetchRequestedAssignments();
                  setShowRequestAccess(false);
                }}
                showBackButton
              />
            </Container>
          ) : requestedAssignments && requestedAssignments.requests.length > 0 ? (
            // Show requests status view
            <RequestsStatusView
              requests={requestedAssignments.requests}
              profileName={profile?.firstName}
              onRequestNewRoleClick={() => setShowRequestAccess(true)}
              isLoading={isFetchingRequestedAssignments}
            />
          ) : (
            // Show sad animation with button if no requests
            <Animation
              title={t(errors.storeNotAssigned.title.key, errors.storeNotAssigned.title.defaultValue)}
              subtitle={t(errors.storeNotAssigned.subtitle.key, errors.storeNotAssigned.subtitle.defaultValue)}
              animation={Animations.SAD}
              button={{
                label: t(errors.storeNotAssigned.button.key, errors.storeNotAssigned.button.defaultValue),
                onClick: () => setShowRequestAccess(true),
              }}
            />
          )}
        </AnimationWrapper>
      )}
      {isFetching && (
        <AnimationWrapper>
          <Animation
            title={t(commonTranslations.loading.key, commonTranslations.loading.defaultValue)}
            animation={Animations.LOADING}
          />
        </AnimationWrapper>
      )}
      {!isFetching &&
        !isError &&
        !isStoresEmpty &&
        !isAssignmentError &&
        !isStorePermissionError &&
        !isRequestedAssignmentsError &&
        isUserPermissionsLoaded && (
          <>
            <Banner
              items={systemBanners}
              onChange={(items) => {
                setSystemBanners(items);
              }}
            />
            <AppContainer isFullScreen={isDesktop}>
              <Banner
                items={bannerItems}
                onChange={(items) => {
                  setBannerItems(items);
                  set({ hasCompletedNewOnboardingItem: false });
                }}
              />
              <Header
                currentPathNameTranslation={currentRouteConfig ? currentRouteConfig?.translation : home}
                onOpenNav={() => {
                  setIsSliderOpen(true);
                  trackEvent(SIDE_MENU_EVENT, 'click', [{ name: 'isMenuOpen', stringValue: 'true' }]);
                }}
              />
              <SideMenu
                header={{
                  profile,
                  storeId: currentStoreId,
                }}
                list={filteredMenuList()}
                isOpen={isSliderOpen}
                onClose={() => {
                  setIsSliderOpen(false);
                  trackEvent(SIDE_MENU_EVENT, 'click', [{ name: 'isMenuOpen', stringValue: 'false' }]);
                }}
              />

              <Routes>
                {filteredMenuList().map(({ links }) =>
                  links.map((link) => (
                    <Route
                      key={link.path}
                      path={link.path}
                      element={
                        <ErrorBoundary
                          FallbackComponent={ErrorFallback}
                          onError={(error, componentStack) =>
                            logError({
                              error,
                              errorType: ErrorType.PAGE_ERROR_BOUNDARY,
                              info: { componentStack, pageRoute: link.path },
                            })
                          }
                        >
                          {link.element}
                        </ErrorBoundary>
                      }
                    />
                  )),
                )}
                <Route
                  path={`/home/${getItem(LocalStorageKey.SelectedStore)}`}
                  element={<Navigate to="/home" />}
                />

                <Route path="*" element={<NotFound />} />
              </Routes>

              {getSpeeddialPermission() && (
                <SpeedDialButton actions={speedDialActions} onAction={handleOnAction} />
              )}

              <SpeedDialIconsWrapper>
                <Chatbot isOpen={isChatbotOpen} closeChatbot={closeChatbot} />
              </SpeedDialIconsWrapper>

              <HomePageSlider isOpen={isHomePageSliderOpen} onClose={closeHomePageSlider} />

              <SpeedDialIconsWrapper>
                <FeedbackForm isOpen={isFeedbackOpen} onClose={closeFeedback} />
              </SpeedDialIconsWrapper>
            </AppContainer>
          </>
        )}
    </>
  );
});
